import { Component, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { createAuditLogKey, LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE } from "@app/audit-log/services/audit-log.constants";
import { AuthUser } from "@app/auth/model/auth-user";
import { Entity } from "@app/entity/model/entity";
import { EntityService } from "@app/entity/services/entity.service";
import { RouterGo } from "@app/root-store/actions";
import { emailValid, firstnameEmpty, humanNameValid, lastnameEmpty } from "@app/shared/validators";
import { AppErrorStateMatcher } from "@app/shared/validators/app-error-state-matcher.service";
import { ROLE_HIERARCHY, ROLE_NAMES, USER_ROLES, UserRole } from "@app/user/model/user-role";
import { UserManagementService } from "@app/user/service/user-management.service";
import { UserActions } from "@app/user/store/actions";
import { combineLatest, Observable, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap, withLatestFrom } from "rxjs/operators";
import { User, UserHelper } from "../../model/user";

@Component({
    selector: "app-user-edit",
    templateUrl: "./user-edit.component.html",
    styleUrls: ["./user-edit.component.scss"],
})
export class UserEditComponent implements OnInit, OnDestroy {
    isModal = false;
    user$: Observable<User>;

    @Output()
    emitter = new EventEmitter<UserActions.UserAction | RouterGo>();

    entities$: Observable<Entity[]>;
    entityIds$: Observable<number[]>;
    userRoles: UserRole[] = [];
    userRoles$: Observable<UserRole[]>;
    userForm: FormGroup = this.formBuilder.group({
        firstName: ["", [firstnameEmpty, humanNameValid]],
        lastName: ["", [lastnameEmpty, humanNameValid]],
        email: ["", emailValid],
        entityId: ["", Validators.required],
        roleId: ["", Validators.required],
    });
    submitAttempted = false;
    public trackById: (item: any) => number | string;
    entityId: number;
    userId: number;

    private currentUserData: User = null;
    private unsubscribe$ = new Subject();

    constructor(
        private formBuilder: FormBuilder,
        public errorMatcher: AppErrorStateMatcher,
        private userService: UserManagementService,
        private readonly route: ActivatedRoute,
        private readonly entityService: EntityService
    ) {}

    ngOnInit() {
        this.userId = +this.route.snapshot.paramMap.get("userId");
        if (this.emitter.observers.length) {
            this.isModal = true;
        }
        this.trackById = (entity: Entity) => this.userService.trackById(entity);
        this.entities$ = this.entityService.selectCurrentEntity$().pipe(
            filter(entity => !!entity),
            tap(entity => {
                this.entityId = entity.id;
                if (!this.userForm.controls.entityId) {
                    this.userForm.patchValue({ entityId: entity.id });
                }
            }),
            switchMap(() => this.entityService.selectEntitiesArray$())
        );
        this.userRoles$ = this.userService.dispatchAndSelectUserRoles().pipe(
            withLatestFrom(this.userService.getAuthUser()),
            map(([roles, user]) => this.filterUserRoles(roles, user)),
            tap(roles => (this.userRoles = roles)),
            take(1)
        );

        this.userService.subscribeToEmitter(this.emitter);

        this.watchFormEmailValueChange();
        this.setUser();
    }

    /**
     * watch form value change
     */
    private watchFormEmailValueChange() {
        this.userForm.controls.email.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                tap((value: string) => this.userForm.controls.email.setValue(value.toLowerCase())),
                switchMap(value => this.userService.validateIfStaffEmailExisted$(value, this.userId)),
                tap((isExisted: boolean) => {
                    this.userForm.controls.firstName.enable();
                    this.userForm.controls.lastName.enable();
                    this.userForm.controls.email.setAsyncValidators(this.userService.emailExistsControl(isExisted));
                    this.userForm.controls.email.updateValueAndValidity();
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();
    }

    updateUserForm() {
        return (user: User) => (user ? this.userForm.patchValue(user) : null);
    }

    canEditUserRole() {
        const isEditable = true;

        combineLatest([this.userService.getCurrentUser(), this.userService.getAuthUser()])
            .pipe(
                filter(([currentUser, authUser]) => !!(currentUser && authUser)),
                take(1)
            )
            .subscribe(([currentUser, authUser]) => {
                if (authUser.roleId !== USER_ROLES.CLARE_ADMIN && currentUser.roleId === USER_ROLES.CLARE_ADMIN) {
                    this.userForm.controls.roleId.setValue(USER_ROLES.CLARE_ADMIN);
                    this.userForm.controls.roleId.disable();
                }
            });
        return isEditable;
    }

    setUser() {
        if (!this.user$) {
            this.user$ = this.userService.getStaffById(this.userId).pipe(
                tap(this.updateUserForm()),
                tap(() => this.canEditUserRole()),
                tap(user => {
                    this.currentUserData = user;
                    if (!!user?.gitkitId) {
                        this.userForm.controls.email.disable();
                    }
                })
            );
        } else {
            this.user$.pipe(take(1)).subscribe(this.updateUserForm());
        }
    }

    cancel() {
        this.emitter.emit(this.userService.cancelAction(this.entityId));
    }

    get fields() {
        return this.userForm.controls;
    }

    saveUser(user: User) {
        if (this.userForm.valid) {
            this.submitAttempted = true;
            const updatedData = this.getUpdateData(user);
            const logData = {
                auditLogName: createAuditLogKey(LOG_CATEGORY.COMPANY, LOG_TYPE.STAFF, LOG_SUB_TYPE.MODIFY),
                entityId: this.entityId,
                data: UserHelper.getModifiedLogString(this.currentUserData, updatedData),
            };

            this.userService.upsertUserData(this.emitter, updatedData, logData);
        } else {
            this.userService.upsertErrorHandling(user);
        }
    }

    private getUpdateData(user: User = {} as User) {
        const formValue = this.userForm.getRawValue();
        const roleId = +formValue.roleId;
        const selectedRole = this.userRoles?.find(r => +r.id === roleId);
        const updatedRole = selectedRole
            ? {
                  role: {
                      ...user.role,
                      name: selectedRole.name,
                  },
              }
            : {};
        return { ...user, ...formValue, roleId, ...updatedRole };
    }

    deleteUser(user: User) {
        this.userService.deleteUser(user, "user-edit");
    }

    resendInvite(user: User) {
        this.userService.resendInvite(user);
    }

    /**
     * Remove options that the user is not allowed to select
     *
     * @param roles
     * @param user
     */
    private filterUserRoles(roles: UserRole[], user: AuthUser): UserRole[] {
        const allowedRoles = ROLE_HIERARCHY[user.role.name];
        roles = roles.filter(r => r.name !== ROLE_NAMES.CLARE_ADMIN);
        if (!allowedRoles) {
            return roles;
        }

        return roles.filter(r => allowedRoles.includes(r.name));
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }
}
